/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faClose,
  faImage,
  faVideo,
} from '@fortawesome/pro-regular-svg-icons';

import { Accordion } from '@client/components/Accordion';
import { Button } from '@client/components/common/inputs/Button';
import { moveArray } from '@client/components/landing-page-builder/helpers';
import { FieldTitle } from './RedStarTitle';
import { LandingMediaDropbox } from './MediaDropBox';
import { useModal } from '@client/components/common/Modal';
import { BeforeAfterModal } from './Modals';
import { FieldDesc } from '../../../pages/CustomProgramPage/common';
import { TextField } from '@client/new/components/basic';
import { UseFormRegisterReturn } from 'react-hook-form';
import { TestimonialsSectionSchema } from '@client/pages/CustomProgramPage/OTPLandingPage.schema';

export const Testimonials = ({
  upload,
  onChange,
  value,
  register: r,
}: {
  upload: (file: File) => Promise<string>;
  onChange: (value: any) => void;
  register: (name: string) => UseFormRegisterReturn;
  value: TestimonialsSectionSchema;
}) => {
  const beforeAfterPopup = useModal(BeforeAfterModal);
  const testimonials = value.testimonials || [];

  const onTestimonialsChange = (testimonials: any) =>
    onChange({ ...value, testimonials });

  const update = (i: number, newVal: any) => {
    onTestimonialsChange([
      ...testimonials.slice(0, i),
      newVal,
      ...testimonials.slice(i + 1),
    ]);
  };

  const remove = (i: number) => {
    onTestimonialsChange([
      ...testimonials.slice(0, i),
      ...testimonials.slice(i + 1),
    ]);
  };

  const move = (from: number, to: number) => {
    onTestimonialsChange(moveArray(testimonials, from, to));
  };

  return (
    <>
      <ul className="text-gray-2 list-disc ml-[20px] text-h12 mb-[20px]">
        <li>
          We recommend adding at least 3 testimonials to your landing page
          before turning this section visible.
        </li>
        <li>Maximum of 11 testimonials.</li>
      </ul>
      <FieldTitle title="Section title" optional />
      <FieldDesc>Maximum of 60 characters</FieldDesc>
      <TextField
        {...r('title')}
        className="max-w-[680px] w-full"
        inputProps={{ maxLength: 60 }}
      />
      <div className="mt-[30px]">
        {testimonials.map((x, i) => {
          return (
            <div
              key={i}
              className="flex space-x-0 mb-4 items-start flex-col sm:flex-row sm:space-x-3"
            >
              <div className="w-[39px] h-[39px] shrink-0 rounded-lg text-lg flex items-center justify-center bg-[#E1E5F0] mb-3 sm:mb-0">
                {i + 1}
              </div>
              <Accordion
                hasError={false}
                title={
                  (x.name || '').length
                    ? `Testimonial from ${x.name}`
                    : 'Testimonial'
                }
                customizations={{ padding: '20px' }}
                maxWidth="1000px"
              >
                <div className="flex">
                  <div className="w-[260px]">
                    <FieldTitle
                      title={'Name'}
                      subtitle={`• Maximum of 50 characters`}
                      optional
                    />
                    <TextField
                      {...r(`testimonials.${i}.name`)}
                      className="w-full"
                      inputProps={{
                        className: `w-full`,
                        maxLength: 50,
                      }}
                      size="small"
                    />
                    <div className="h-[10px]" />
                    <FieldTitle
                      title={'Testimonial'}
                      subtitle={`• Maximum of 2000 characters`}
                      optional
                    />
                    <TextField
                      className="w-full"
                      {...r(`testimonials.${i}.text`)}
                      multiline
                      rows={6}
                      inputProps={{
                        className: 'resize-none',
                        maxLength: 2000,
                      }}
                    />
                  </div>
                  <div className="ml-[30px]">
                    <FieldTitle
                      title={'Before / After Photo'}
                      showTooltip={() => beforeAfterPopup.open()}
                      optional
                    />
                    <LandingMediaDropbox
                      upload={upload}
                      label={''}
                      validationText="You can create beautiful before/after photos using our guide here."
                      value={x.image}
                      onChange={(image) => update(i, { ...x, image })}
                      type="image"
                      icon={faImage as any}
                      variant="white"
                    >
                      <ul className="text-black list-none text-center text-h12">
                        <li>JPG File</li>
                        <li>Square image</li>
                      </ul>
                    </LandingMediaDropbox>
                  </div>
                  <div className="ml-[30px]">
                    <FieldTitle title={'Intro video'} optional />
                    <LandingMediaDropbox
                      upload={upload}
                      label={''}
                      validationText="Optionally, provide a video testimonial provided by your client."
                      value={x.video}
                      onChange={(video) => update(i, { ...x, video })}
                      type="video"
                      icon={faVideo as any}
                      variant="white"
                      errorMsg=""
                    >
                      <ul className="text-black list-none text-center text-h12">
                        <li>Max 90 seconds</li>
                        <li>Max 100mb</li>
                      </ul>
                    </LandingMediaDropbox>
                  </div>
                </div>
              </Accordion>

              <div className="flex items-center h-[39px] pl-[28px]">
                <div className="w-[40px]">
                  {i > 0 && testimonials.length > 1 ? (
                    <FontAwesomeIcon
                      icon={faChevronUp as any}
                      onClick={() => move(i, i - 1)}
                      className="-mb-0.5 cursor-pointer"
                    />
                  ) : null}
                </div>
                <div className="w-[40px]">
                  {i < testimonials.length - 1 && testimonials.length > 1 ? (
                    <FontAwesomeIcon
                      icon={faChevronDown as any}
                      onClick={() => move(i, i + 1)}
                      className="cursor-pointer"
                    />
                  ) : null}
                </div>
                <div className="w-[40px]">
                  <FontAwesomeIcon
                    icon={faClose as any}
                    onClick={() => remove(i)}
                    className="cursor-pointer"
                  />
                </div>
              </div>
            </div>
          );
        })}
        {testimonials.length < 11 ? (
          <Button
            onClick={() =>
              onTestimonialsChange([
                ...testimonials,
                { name: '', text: '', image: null, video: null },
              ])
            }
          >
            Add Testimonial
          </Button>
        ) : null}
      </div>
      <beforeAfterPopup.Popup />
    </>
  );
};
